import React from 'react';
import styled, { useTheme } from 'styled-components';

import { FjTableCell as Cell, FjTableRow as Row, FjTable as Table } from 'components/primitives';
import { getContactEmail } from 'constants/contact-emails';

// Matches AuthUtils.getSeatUsageForProductAccess's return shape (@portal/portal-core) exactly -
// this component exists to render that shape verbatim, using its own field names as labels,
// rather than each caller re-deriving/re-labeling a subset of it.
export interface SeatUsage {
  maxSeats: number;
  maxShared: number;
  allocatedSeats: number;
  openSeats: number;
  openSharedSeats: number;
  activeUserSeats: number;
  activeSharedSeats: number;
  pendingUserInvites: number;
}

export interface SeatSummaryProps {
  name: string;
  Icon: React.ComponentType<{ size?: string }> | null;
  seats: SeatUsage;
  productName: string;
}

const ReportsContainer = styled.div`
  display: flex;
  flex: 1;
  width: 100%;
  min-height: fit-content;
  max-height: 650px;
  justify-content: center;
  align-items: center;
  padding: 40px 0px;
`;

const CenterContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: 600px;
`;

const TableHeader = styled.div`
  margin-left: 5px;
`;

const LabelCell = styled(Cell)`
  justify-content: flex-end;
`;

const ContactSales = styled.div`
  font-size: ${({ theme }) => theme.fontSizes.h2};
  margin-top: 10px;
`;

// `_rowIndex`/`_colIndex` are injected at runtime by the parent Row via cloneElement, so they're
// optional at these JSX call sites.
type CellProps = React.ComponentProps<typeof Cell>;
const TableCell = Cell as React.ComponentType<
  Omit<CellProps, '_rowIndex' | '_colIndex'> & Partial<Pick<CellProps, '_rowIndex' | '_colIndex'>>
>;

// The institutional cost-center admin's own "License Seat Summary" view (Reports tab) — the
// single source of truth for what "seat summary" means in this product, reused verbatim as the
// SuperAdmin-facing preview (see SeatSummaryHelpButton) so both audiences see the same numbers in
// the same language, not two independently-maintained renderings.
export const SeatSummary = ({
  name,
  Icon,
  seats,
  productName,
}: SeatSummaryProps): React.ReactElement => {
  const {
    maxSeats,
    maxShared,
    allocatedSeats,
    openSeats,
    openSharedSeats,
    activeUserSeats,
    activeSharedSeats,
    pendingUserInvites,
  } = seats;

  const theme = useTheme();
  const tableStyle = {
    // Only 2 explicit columns, matching the original layout: the maxShared>0 rows place a 3rd
    // (detail) cell that falls into an implicit column, which FjTable's TableContainer now sizes
    // safely (grid-auto-columns: minmax(0, 1fr)) instead of an unconstrained "auto" - this avoids
    // both text overflow in a narrow container AND reserving dead 3rd-column space on the
    // maxShared=0 rows, which never place a 3rd cell at all.
    gridTemplateColumns: '200px minmax(0, 1fr)',
    fontSize: '16px',
    borderColor: theme.colors.selected,
    // A `minmax` floor keeps the original compact row height when content fits on one line,
    // while letting rows grow instead of clipping/overlapping once text wraps to more than one.
    rowHeight: 'minmax(50px, auto)',
  };
  const tableHeaderInfo = `${name} License Seat Summary`;
  const emailTo = `mailto:${getContactEmail(productName)}`;

  const header = (
    <Row>
      <TableCell span="*">
        {Icon && <Icon size="40px" />}
        <TableHeader>{tableHeaderInfo}</TableHeader>
      </TableCell>
    </Row>
  );
  const footer = (
    <ContactSales>
      <a href={emailTo}>Contact Sales</a> to purchase additional seats.
    </ContactSales>
  );

  // A row's 3rd (detail) cell only appears when there's Shared Computer info to break out.
  // FjTableRow maps every child it's given straight into a cloned FjTableCell with no filtering,
  // so an omitted cell must be left out of the children array entirely - not included as a falsy
  // `{condition && <Cell/>}` expression, which FjTableRow can't tolerate. FjTable's
  // grid-auto-columns handles the resulting variable column count per row (see the `tableStyle`
  // comment above), so this is safe to toggle per row instead of needing two full copies of the
  // table.
  const detailCell = (key: string, content: React.ReactNode): React.ReactElement => (
    <TableCell key={key}>
      <i>{content}</i>
    </TableCell>
  );

  const purchasedCells = [
    <LabelCell key="label">Purchased:</LabelCell>,
    <TableCell key="value">{maxSeats}</TableCell>,
    maxShared > 0 && detailCell('detail', <>Up to {maxShared} for Shared Computers</>),
  ].filter((cell): cell is React.ReactElement => !!cell);

  const assignedCells = [
    <LabelCell key="label">Assigned:</LabelCell>,
    <TableCell key="value">{allocatedSeats}</TableCell>,
    detailCell(
      'detail',
      maxShared > 0 ? (
        <>
          Users = {activeUserSeats}, Shared Computers = {activeSharedSeats}, Pending Invites ={' '}
          {pendingUserInvites}
        </>
      ) : (
        <>
          Users = {activeUserSeats}, Pending Invites = {pendingUserInvites}
        </>
      ),
    ),
  ];

  const availableCells = [
    <LabelCell key="label">Available:</LabelCell>,
    <TableCell key="value">{openSeats}</TableCell>,
    maxShared > 0 && detailCell('detail', <>{openSharedSeats} for Shared Computers.</>),
  ].filter((cell): cell is React.ReactElement => !!cell);

  return (
    <ReportsContainer>
      <CenterContent>
        <Table style={tableStyle}>
          {header}
          <Row>{purchasedCells}</Row>
          <Row>{assignedCells}</Row>
          <Row>{availableCells}</Row>
        </Table>
        {footer}
      </CenterContent>
    </ReportsContainer>
  );
};

export default SeatSummary;
